<template>
    <div class="d-inline pr-2 pl-2">
        <!-- NOTE - Button -->
        <b-button variant="primary" class="mb-2 w-100" @click="openModal"
            ><i class="fas fa-edit mb-1"></i>Manage Hashtag Set Groups</b-button
        >

        <!-- NOTE - Manage Groups Modal -->
        <b-modal
            size="lg"
            ref="manage-hashtagsetgroup-modal"
            id="manage-hashtagsetgroup-modal"
            centered
            title="Manage your Hashtag Set Groups"
        >
            <!-- Modal preloader -->
            <PagePreloader v-if="modalisloading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="errors"></CustomAlerts>

            <!-- Info -->
            <InfoBox
                text="Move your Hashtag Sets from one Group to another by dragging and dropping them!"
            ></InfoBox>

            <!-- NOTE - Start draggable -->
            <template v-for="group in groups">
                <h4>
                    {{ group.emoji }} {{ group.title }}
                    <!-- NOTE - Show options if we're other than uncategorized -->
                    <template v-if="!['uncategorized', 'shared'].includes(group.id)">
                        <!-- NOTE - Open DELETE modal -->
                        <div class="symbol symbol-30 symbol-light-danger ml-3">
                            <a @click="openDeleteGroupModal(group)">
                                <span class="symbol-label font-weight-bold">
                                    <i class="fas fa-times font-size-sm text-danger"></i>
                                </span>
                            </a>
                        </div>
                        <!-- NOTE - Open DELETE modal -->
                        <div class="symbol symbol-30 symbol-light-success ml-3">
                            <a @click="openUpdateSingleGroupModal(group)">
                                <span class="symbol-label font-weight-bold">
                                    <i class="fas fa-edit font-size-sm text-success"></i>
                                </span>
                            </a>
                        </div>
                    </template>
                </h4>
                <draggable
                    :list="group.sets"
                    group="manageModalGroup"
                    class="alert row shadow-sm mx-1 gutter-b minh50"
                >
                    <span
                        class="btn btn-sm btn-font-sm font-weight-bold m-1 btn-light-success"
                        v-for="myhashtag in group.sets"
                        >{{ myhashtag.emoji }} {{ myhashtag.title }}</span
                    >
                </draggable>
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    type="Submit"
                    variant="success"
                    :class="
                        modalisloading ? 'spinner spinner-darker-success spinner-left mr-3' : ''
                    "
                    @click="updateHashtagGroups"
                    :disabled="modalisloading"
                >
                    Update groups
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>

        <!-- NOTE - Delete group Modal -->
        <b-modal
            size="lg"
            ref="delete-hashtagsetgroup-modal"
            id="delete-hashtagsetgroup-modal"
            centered
            title="Are you sure you want to delete this Hashtag Group?"
        >
            <!-- Modal preloader -->
            <PagePreloader v-if="modalisloading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="errors"></CustomAlerts>

            <template v-if="deletemodal.sets">
                <p class="lead">"{{ deletemodal.emoji }}{{ deletemodal.title }}"</p>
                <sub
                    >This Hashtag Set Group contains
                    <code>{{ deletemodal.sets.length }}</code> hashtag sets</sub
                >
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    variant="danger"
                    @click="deleteHashtagGroup(deletemodal)"
                    :disabled="modalisloading"
                >
                    Delete Hashtag Group
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>

        <!-- NOTE - UPDATE group modal -->
        <b-modal
            size="lg"
            ref="update-hashtagsetgroup-modal"
            id="update-hashtagsetgroup-modal"
            centered
            title="Update your existing Hashtag Group"
        >
            <!-- Modal preloader -->
            <PagePreloader v-if="modalisloading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="errors"></CustomAlerts>

            <!-- FORM -->
            <form method="POST" v-on:submit.prevent="updateHashtagGroup">
                <!-- Emoji picker -->
                <b-form-group
                    label-cols="4"
                    label-cols-lg="2"
                    label="Icon"
                    label-for="input-default"
                >
                    <emojiPicker
                        :propCurrentEmoji="updatemodal.emoji"
                        @emitSavedEmoji="handleEmitEmoji"
                    ></emojiPicker>
                </b-form-group>

                <b-form-group
                    label-cols="4"
                    label-cols-lg="2"
                    label="Title"
                    label-for="input-default"
                >
                    <b-form-input
                        id="title"
                        name="title"
                        v-model="updatemodal.title"
                    ></b-form-input>
                </b-form-group>

                <input type="hidden" name="id" v-bind:value="updatemodal.id" />
                <input type="submit" class="d-none" :disabled="modalisloading" />
            </form>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button variant="danger" @click="updateHashtagGroup" :disabled="modalisloading">
                    Update Hashtag Group
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
    </div>
</template>
<style scoped>
.minh50 {
    min-height: 50px;
}
</style>
<script>
// Defaults
import axios from "axios";
import { mapGetters } from "vuex";
// Draggable
import draggable from "vuedraggable";
// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import emojiPicker from "@/view/content/Custom/emojiPicker.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
// Mixins
import axiosErrorManagement from "@/mixins/axiosErrorManagement";

export default {
    name: "buttonManageHashtagGroups",
    mixins: [axiosErrorManagement],
    components: {
        CustomAlerts,
        PagePreloader,
        draggable,
        emojiPicker,
        InfoBox,
    },
    props: {
        propGroups: Array,
        propSets: Array,
    },
    data() {
        return {
            // Errors
            errors: [],

            // Displayers
            modalisready: false,

            // Modals
            deletemodal: {},
            updatemodal: {},
            selectedEmoji: "",

            // Data
            groups: [],

            // Preloaders
            modalisloading: false,
        };
    },
    methods: {
        // Generate array
        generateGroupArray() {
            // Empty base array
            this.groups = [];

            // Loop through mygroups
            this.propGroups.forEach((currentItem) => {
                this.groups.push({
                    id: currentItem.id,
                    title: currentItem.title,
                    emoji: currentItem.emoji,
                    sets: [],
                });
            });

            // Add uncategorized subarray
            this.groups.push({
                id: "uncategorized",
                title: "Uncategorized",
                emoji: "",
                sets: [],
            });
            // this.groups.push({
            //     id: "shared",
            //     title: "Shared",
            //     emoji: "",
            //     sets: [],
            // });

            // Loop through all your existing groups
            this.groups.forEach((part, index, theArray) => {
                // Add an empty sets array to each
                // theArray[index].sets = [];

                // Loop through hashtags to add them
                this.propSets.forEach((currentItem) => {
                    // Check to see if this hashtag set belongs to the right user (to hide shared hashtag sets)
                    if (currentItem.userid == this.currentUser.id) {
                        // Add as we need
                        if (part.id == currentItem.set_parent) {
                            theArray[index].sets.push({
                                id: currentItem.id,
                                title: currentItem.title,
                                emoji: currentItem.emoji,
                            });
                        }
                    }
                });
            });
        },

        // Open modal
        openModal() {
            // Refresh errors
            this.errors = [];

            // Generate
            this.generateGroupArray();

            // Open modal
            this.$refs["manage-hashtagsetgroup-modal"].show();
        },

        /**
         * SECTION - Update all the hashtag groups
         */
        updateHashtagGroups() {
            // Reset errors
            this.errors = [];

            // Set loader
            this.modalisloading = true;

            // Perform post
            axios
                .post("myhashtagsv2/groups/update/all", {
                    groups: this.groups,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Handle
                    if (data.status == "OK") {
                        // Emit if successful
                        this.refreshHashtags();

                        // Close modal
                        this.$refs["manage-hashtagsetgroup-modal"].hide();
                    }

                    // Handle error messages
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.modalisloading = false;
                });
        },

        /**
         * SECTION - Open modal for editing SINGLE hashtag Group
         */
        openUpdateSingleGroupModal(group) {
            // Refresh errors
            this.errors = [];
            this.modalisloading = false;

            // Reset emoji
            this.resetSelectedEmoji();

            // Set data
            this.updatemodal = group;
            this.selectedEmoji = this.updatemodal.emoji;

            // Open modal
            this.$refs["update-hashtagsetgroup-modal"].show();
        },

        /**
         * SECTION - Update SINGLE hashtag group
         */
        updateHashtagGroup() {
            // Reset errors
            this.errors = [];

            // Set loader
            this.modalisloading = true;

            // Set emoji to the one we selected
            this.updatemodal.emoji = this.selectedEmoji;

            // Perform post
            axios
                .post("myhashtagsv2/groups/update/single", {
                    id: this.updatemodal.id,
                    title: this.updatemodal.title,
                    emoji: this.updatemodal.emoji,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Handle
                    if (data.status == "OK") {
                        // Emit if successful
                        this.refreshHashtags();

                        // FIND and modify our group
                        var key = this.groups.findIndex((x) => x.id == this.updatemodal.id);

                        // Update values
                        this.groups[key].title = this.updatemodal.title;
                        this.groups[key].emoji = this.updatemodal.emoji;

                        // Close modal
                        this.$refs["update-hashtagsetgroup-modal"].hide();
                    }

                    // Handle error messages
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.modalisloading = false;
                });
        },

        openDeleteGroupModal(group) {
            // Set errors
            this.errors = [];

            // Set deletemodal group
            this.deletemodal = group;

            // Open modal
            this.$refs["delete-hashtagsetgroup-modal"].show();
        },

        deleteHashtagGroup(group) {
            // Reset errors
            this.errors = [];

            // Set loader
            this.modalisloading = true;

            // Perform post
            axios
                .post("myhashtagsv2/groups/delete", {
                    id: group.id,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Handle
                    if (data.status == "OK") {
                        // Find the set we need to move
                        var key = this.groups.indexOf(group, 0);

                        // Get sets we need to move
                        var setsToMove = this.groups[key].sets;

                        // Check length
                        if (setsToMove.length > 0) {
                            // Loop through and move everything to the uncategorized array
                            setsToMove.forEach((currentItem) => {
                                this.groups[this.groups.length - 1].sets.push(currentItem);
                            });
                        }

                        // Remove the actual group from our view
                        this.groups.splice(key, 1);

                        // Emit if successful
                        this.refreshHashtags();

                        // Close modal
                        this.$refs["delete-hashtagsetgroup-modal"].hide();
                    }

                    // Handle error messages
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.modalisloading = false;
                });
        },

        resetSelectedEmoji() {
            this.selectedEmoji = null;
        },

        handleEmitEmoji(emoji) {
            this.selectedEmoji = emoji;
        },

        refreshHashtags() {
            this.$emit("groupManageSuccess");
        },

        handleEmitEmoji(emoji) {
            this.selectedEmoji = emoji;
        },
    },
    computed: {
        ...mapGetters({ currentUser: "auth/currentUser" }),
    },
};
</script>
